import React from 'react';
import PropTypes from 'prop-types';
import { MapPin } from 'lucide-react';
import {
  FormInput,
  FormSelect,
  PhoneInput,
  SectionHeader,
  FormNavigation,
  FormRow,
  FormCheckbox,
} from '../FormComponents';

const stateOptions = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

const yearsOptions = [
  { value: '0', label: 'Less than 1 year' },
  { value: '1', label: '1 year' },
  { value: '2', label: '2 years' },
  { value: '3', label: '3 years' },
  { value: '4', label: '4 years' },
  { value: '5', label: '5+ years' },
];

export default function ContactInfo({ data, onChange, onNext, onBack }) {
  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const needsPreviousAddress = parseInt(data.yearsAtAddress || '0') < 2;

  let isValid =
    Boolean(data.streetAddress?.trim()) &&
    Boolean(data.city?.trim()) &&
    Boolean(data.state) &&
    Boolean(data.zipCode?.length === 5) &&
    Boolean(data.yearsAtAddress) &&
    Boolean(data.phone?.length === 10) &&
    Boolean(data.email?.includes('@'));

  if (needsPreviousAddress) {
    isValid = isValid &&
      Boolean(data.prevStreetAddress?.trim()) &&
      Boolean(data.prevCity?.trim()) &&
      Boolean(data.prevState) &&
      Boolean(data.prevZipCode?.length === 5);
  }

  return (
    <div>
      <SectionHeader
        title="Contact Information"
        subtitle="Where can we reach you?"
        icon={<MapPin size={28} />}
      />

      <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#e2e8f0', marginBottom: '16px' }}>
        Current Address
      </h3>

      <FormInput
        label="Street Address"
        value={data.streetAddress || ''}
        onChange={(value) => updateField('streetAddress', value)}
        placeholder="123 Main Street"
        required
        autoComplete="street-address"
      />

      <FormInput
        label="Apartment/Unit (optional)"
        value={data.unit || ''}
        onChange={(value) => updateField('unit', value)}
        placeholder="Apt 4B"
        autoComplete="address-line2"
      />

      <FormRow>
        <FormInput
          label="City"
          value={data.city || ''}
          onChange={(value) => updateField('city', value)}
          placeholder="Los Angeles"
          required
          autoComplete="address-level2"
        />
        <FormSelect
          label="State"
          value={data.state || ''}
          onChange={(value) => updateField('state', value)}
          options={stateOptions}
          placeholder="Select state"
          required
        />
        <FormInput
          label="ZIP Code"
          value={data.zipCode || ''}
          onChange={(value) => updateField('zipCode', value.replace(/\D/g, '').slice(0, 5))}
          placeholder="90210"
          required
          inputMode="numeric"
          maxLength={5}
          autoComplete="postal-code"
        />
      </FormRow>

      <FormSelect
        label="Years at Current Address"
        value={data.yearsAtAddress || ''}
        onChange={(value) => updateField('yearsAtAddress', value)}
        options={yearsOptions}
        placeholder="Select duration"
        required
      />

      {needsPreviousAddress && (
        <>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#e2e8f0', margin: '24px 0 16px' }}>
            Previous Address
            <span style={{ fontSize: '12px', fontWeight: 400, color: '#94a3b8', marginLeft: '8px' }}>
              (Required if less than 2 years at current address)
            </span>
          </h3>

          <FormInput
            label="Street Address"
            value={data.prevStreetAddress || ''}
            onChange={(value) => updateField('prevStreetAddress', value)}
            placeholder="456 Oak Avenue"
            required
          />

          <FormRow>
            <FormInput
              label="City"
              value={data.prevCity || ''}
              onChange={(value) => updateField('prevCity', value)}
              placeholder="San Francisco"
              required
            />
            <FormSelect
              label="State"
              value={data.prevState || ''}
              onChange={(value) => updateField('prevState', value)}
              options={stateOptions}
              placeholder="Select state"
              required
            />
            <FormInput
              label="ZIP Code"
              value={data.prevZipCode || ''}
              onChange={(value) => updateField('prevZipCode', value.replace(/\D/g, '').slice(0, 5))}
              placeholder="94102"
              required
              inputMode="numeric"
              maxLength={5}
            />
          </FormRow>
        </>
      )}

      <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#e2e8f0', margin: '24px 0 16px' }}>
        Contact Details
      </h3>

      <FormRow>
        <PhoneInput
          label="Phone Number"
          value={data.phone || ''}
          onChange={(value) => updateField('phone', value)}
          required
        />
        <FormInput
          label="Email Address"
          type="email"
          value={data.email || ''}
          onChange={(value) => updateField('email', value)}
          placeholder="john@example.com"
          required
          autoComplete="email"
        />
      </FormRow>

      <FormCheckbox
        label="I agree to receive communications via email and text"
        checked={data.agreeToContact || false}
        onChange={(value) => updateField('agreeToContact', value)}
        description="We'll send important updates about your application"
      />

      <FormNavigation
        onBack={onBack}
        onNext={onNext}
        isValid={isValid}
      />
    </div>
  );
}

ContactInfo.propTypes = {
  data: PropTypes.shape({
    streetAddress: PropTypes.string,
    unit: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
    yearsAtAddress: PropTypes.string,
    prevStreetAddress: PropTypes.string,
    prevCity: PropTypes.string,
    prevState: PropTypes.string,
    prevZipCode: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    agreeToContact: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};
